// Small helper for the "catch (error: unknown)" migration. Extracts the most
// informative human-readable message from an unknown value (usually an Axios
// error), falling back to a caller-supplied default.

interface WithMessage { message?: string }
interface WithAxiosResponse {
  response?: { data?: WithMessage & { error?: string } };
  message?: string;
}

/**
 * Extract the most informative human-readable message from an unknown error.
 *
 * Handles the common shapes that turn up in a `catch (error: unknown)` block:
 * plain strings, `Error` instances, and Axios errors (which nest the useful
 * message under `response.data.message` or `response.data.error`). Falls back
 * to the caller-supplied string if none of those are present.
 *
 * @param e - the value caught in a try/catch — usually an `unknown`.
 * @param fallback - message returned when nothing better can be extracted.
 * @returns the resolved message string, never `undefined`.
 * @example
 *   try { await api.login(...); }
 *   catch (err) { toast.error(getErrorMessage(err, 'Login failed')); }
 */
export function getErrorMessage(e: unknown, fallback: string): string {
  if (typeof e === 'string') return e;
  if (typeof e === 'object' && e !== null) {
    const anyE = e as WithAxiosResponse;
    return (
      anyE.response?.data?.message ??
      anyE.response?.data?.error ??
      anyE.message ??
      fallback
    );
  }
  return fallback;
}
